import { useEffect, useRef } from "react";

const rand = (a, b) => a + Math.random() * (b - a);

export const SparkleBurst = () => {
  const sparks = Array.from({ length: 48 }, (_, i) => {
    const angle = (i / 48) * Math.PI * 2 + rand(-0.1, 0.1);
    const dist = rand(120, 380);
    return {
      id: i,
      style: {
        "--dx": `${Math.cos(angle) * dist}px`,
        "--dy": `${Math.sin(angle) * dist}px`,
        "--s": rand(4, 10).toFixed(1) + "px",
        animationDelay: `${rand(0, 0.25).toFixed(2)}s`,
        animationDuration: `${rand(1.4, 2.4).toFixed(2)}s`,
      },
    };
  });
  return (
    <div className="sparkle-burst" data-testid="sparkle-burst" aria-hidden="true">
      {sparks.map((s) => <span key={s.id} className="spark" style={s.style} />)}
    </div>
  );
};

const CONFETTI_COLORS = ["#e8c77a", "#f6dfa1", "#1fb8a6", "#b9a4ff", "#ffffff", "#d9b25e"];

export const ConfettiRain = () => {
  const pieces = Array.from({ length: 120 }, (_, i) => ({
    id: i,
    style: {
      left: `${rand(0, 100)}%`,
      background: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
      width: `${rand(6, 11).toFixed(0)}px`,
      height: `${rand(10, 18).toFixed(0)}px`,
      "--drift": `${rand(-80, 80).toFixed(0)}px`,
      "--spin": `${rand(360, 1080).toFixed(0)}deg`,
      animationDelay: `${rand(0, 1.5).toFixed(2)}s`,
      animationDuration: `${rand(3, 5.5).toFixed(2)}s`,
    },
  }));
  return (
    <div className="confetti-rain" data-testid="confetti-rain" aria-hidden="true">
      {pieces.map((p) => <span key={p.id} className="confetti" style={p.style} />)}
    </div>
  );
};

const FIREWORK_COLORS = ["#f6dfa1", "#e8c77a", "#ffffff", "#1fb8a6", "#b9a4ff", "#ff9fd0"];

const launch = (w, h) => ({
  x: rand(w * 0.15, w * 0.85), y: h, tx: rand(h * 0.12, h * 0.45),
  vy: -rand(h * 0.011, h * 0.015), color: FIREWORK_COLORS[Math.floor(rand(0, FIREWORK_COLORS.length))],
});

const explode = (r, particles, scale) => {
  const n = 90;
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2, sp = rand(2, 7) * scale;
    particles.push({ x: r.x, y: r.y, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp, life: 1, decay: rand(0.012, 0.02), color: r.color });
  }
};

export const Fireworks = ({ duration = 8000 }) => {
  const ref = useRef(null);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const canvas = ref.current, ctx = canvas.getContext("2d");
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const resize = () => { canvas.width = innerWidth * dpr; canvas.height = innerHeight * dpr; ctx.setTransform(dpr, 0, 0, dpr, 0, 0); };
    resize();
    window.addEventListener("resize", resize);
    const rockets = [launch(innerWidth, innerHeight), launch(innerWidth, innerHeight)], particles = [], start = performance.now();
    let last = 0, prev = start, raf;
    const frame = (t) => {
      const k = Math.min((t - prev) / 16.7, 3);
      prev = t;
      const w = innerWidth, h = innerHeight, active = t - start < duration;
      if (active && t - last > rand(280, 650)) { rockets.push(launch(w, h)); last = t; }
      ctx.clearRect(0, 0, w, h);
      ctx.globalCompositeOperation = "lighter";
      for (let i = rockets.length - 1; i >= 0; i--) {
        const r = rockets[i];
        r.y += r.vy * k;
        ctx.fillStyle = r.color;
        ctx.fillRect(r.x - 1.5, r.y, 3, 10);
        if (r.y <= r.tx) { explode(r, particles, Math.max(0.8, Math.min(w, h) / 600)); rockets.splice(i, 1); }
      }
      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i];
        p.vx *= 0.98 ** k; p.vy = p.vy * 0.98 ** k + 0.05 * k; p.x += p.vx * k; p.y += p.vy * k; p.life -= p.decay * k;
        if (p.life <= 0) { particles.splice(i, 1); continue; }
        ctx.globalAlpha = p.life;
        ctx.fillStyle = p.color;
        ctx.beginPath(); ctx.arc(p.x, p.y, 2.6, 0, Math.PI * 2); ctx.fill();
      }
      ctx.globalAlpha = 1;
      if (active || rockets.length || particles.length) raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", resize); };
  }, [duration]);

  return <canvas ref={ref} className="fireworks-canvas" data-testid="fireworks" aria-hidden="true" />;
};
